import React from 'react';
import { 
  Box, 
  Heading, 
  Text, 
  VStack, 
  HStack, 
  Table, 
  Thead, 
  Tbody, 
  Tr, 
  Th, 
  Td, 
  Badge, 
  Card, 
  CardHeader, 
  CardBody, 
  Link, 
  Tooltip, 
  Accordion, 
  AccordionItem, 
  AccordionButton, 
  AccordionPanel, 
  AccordionIcon,
  List,
  ListItem,
  ListIcon
} from '@chakra-ui/react';
import { ExternalLinkIcon, WarningIcon, CheckCircleIcon, InfoIcon } from '@chakra-ui/icons';
import ReactMarkdown from 'react-markdown';

const MedicalNecessityPanel = ({ results, hideScores = false, highlightCodes = [] }) => {
  if (!results) return null;

  return (
    <VStack spacing={6} align="stretch">
      
      {/* Summary Cards */}
      <HStack spacing={6} align="stretch" flexWrap="wrap">
        {!hideScores && (results.approval_assessment_score !== undefined || results.approval_assessment_score_v2 !== undefined) && (
          <Card flex={1} minW="250px" borderLeft="4px solid" borderColor={
            (results.approval_assessment_score_v2 ?? results.approval_assessment_score) >= 80 ? "green.400" : 
            (results.approval_assessment_score_v2 ?? results.approval_assessment_score) >= 60 ? "yellow.400" : "red.400"
          }>
            <CardHeader pb={2}>
              <Heading size="sm" color="gray.500" textTransform="uppercase">Likelihood of Payment</Heading>
            </CardHeader>
            <CardBody pt={0}>
              <VStack align="start" spacing={3}>
                {results.approval_assessment_score !== undefined && (
                  <Box>
                    <HStack align="baseline" spacing={2}>
                      <Text fontSize="2xl" fontWeight="bold" color={results.approval_assessment_score >= 80 ? "green.600" : results.approval_assessment_score >= 60 ? "yellow.600" : "red.600"}>
                        {results.approval_assessment_score}/100
                      </Text>
                      <Badge colorScheme="gray" fontSize="2xs">Legacy</Badge>
                    </HStack>
                    {results.approval_assessment && (
                      <Tooltip label={results.approval_assessment} placement="bottom" hasArrow>
                        <Text fontSize="xs" color="gray.500" mt={1} noOfLines={2} cursor="help">
                          {results.approval_assessment}
                        </Text>
                      </Tooltip>
                    )}
                  </Box>
                )}

                {results.approval_assessment_score_v2 !== undefined && (
                  <Box>
                    <HStack align="baseline" spacing={2}>
                      <Text fontSize="lg" fontWeight="bold" color={results.approval_assessment_score_v2 >= 80 ? "green.600" : results.approval_assessment_score_v2 >= 60 ? "yellow.600" : "red.600"}>
                        {results.approval_assessment_score_v2}/100
                      </Text>
                      <Badge colorScheme="purple" fontSize="2xs">V2</Badge>
                    </HStack>
                    {results.approval_assessment_v2 && (
                      <Tooltip label={results.approval_assessment_v2} placement="bottom" hasArrow>
                        <Text fontSize="xs" color="gray.400" mt={1} noOfLines={1} cursor="help">
                          {results.approval_assessment_v2}
                        </Text>
                      </Tooltip>
                    )}
                  </Box>
                )}
              </VStack>
            </CardBody>
          </Card>
        )}

        {!hideScores && results.needs_review_score !== undefined && (
          <Card flex={1} minW="250px" borderLeft="4px solid" borderColor={results.needs_review_score >= 80 ? "red.400" : results.needs_review_score >= 40 ? "yellow.400" : "green.400"}>
            <CardHeader pb={2}>
              <Heading size="sm" color="gray.500" textTransform="uppercase">Needs Review Score</Heading>
            </CardHeader>
            <CardBody pt={0}>
              <VStack align="start" spacing={3}>
                <Box>
                  <HStack align="baseline" spacing={2}>
                    <Text fontSize="2xl" fontWeight="bold" color={results.needs_review_score >= 80 ? "red.600" : results.needs_review_score >= 40 ? "yellow.600" : "green.600"}>
                      {results.needs_review_score}/100
                    </Text>
                    <Badge colorScheme="gray" fontSize="2xs">Legacy</Badge>
                  </HStack>
                  {results.needs_review_rationale && (
                    <Tooltip label={results.needs_review_rationale} placement="bottom" hasArrow>
                      <Text fontSize="xs" color="gray.500" mt={1} noOfLines={2} cursor="help">
                        {results.needs_review_rationale}
                      </Text>
                    </Tooltip>
                  )}
                </Box>

                {results.dx_review_required_score !== undefined && (
                  <Box>
                    <HStack align="baseline" spacing={2}>
                      <Text fontSize="lg" fontWeight="bold" color={results.dx_review_required_score >= 80 ? "red.600" : results.dx_review_required_score >= 40 ? "yellow.600" : "green.600"}>
                        {results.dx_review_required_score}/100
                      </Text>
                      <Badge colorScheme="purple" fontSize="2xs">V2 Dx Review</Badge>
                    </HStack>
                    {results.dx_review_rationale && (
                      <Tooltip label={results.dx_review_rationale} placement="bottom" hasArrow>
                        <Text fontSize="xs" color="gray.600" mt={1} noOfLines={2} cursor="help">
                          {results.dx_review_rationale}
                        </Text>
                      </Tooltip>
                    )}
                  </Box>
                )}
              </VStack>
            </CardBody>
          </Card>
        )}

        <Card flex={1} minW="250px">
          <CardHeader pb={2}>
            <Heading size="sm" color="gray.500" textTransform="uppercase">Coverage Basis</Heading>
          </CardHeader>
          <CardBody pt={0}>
            <Text fontSize="2xl" fontWeight="bold">{results.coverage_basis}</Text>
            <Text fontSize="sm" color="gray.500" mt={1}>
              Policies Checked: {results.policy_ids_checked?.join(', ') || 'None'}
            </Text>
            {results.commercial_policy_urls && results.commercial_policy_urls.length > 0 && (
              <Box mt={2}>
                <Text fontSize="xs" fontWeight="bold" color="gray.600" mb={1}>Commercial Sources:</Text>
                <VStack align="start" spacing={1}>
                  {results.commercial_policy_urls.map((doc, idx) => (
                    <Link key={idx} href={doc.url} isExternal color="blue.500" fontSize="xs">
                      {doc.title} <ExternalLinkIcon mx="2px" />
                    </Link>
                  ))}
                </VStack>
              </Box>
            )}
          </CardBody>
        </Card>

        <Card flex={1} minW="250px">
          <CardHeader pb={2}>
            <Heading size="sm" color="gray.500" textTransform="uppercase">MAC Jurisdiction</Heading>
          </CardHeader>
          <CardBody pt={0}>
            <Text fontSize="lg" fontWeight="medium">{results.mac_jurisdiction}</Text>
            <Text fontSize="sm" color="gray.500" mt={1}>
              Service Date: {results.service_date || 'Unknown'}
            </Text>
          </CardBody>
        </Card>

        <Card flex={1} minW="250px">
          <CardHeader pb={2}>
            <Heading size="sm" color="gray.500" textTransform="uppercase">Case Context</Heading>
          </CardHeader>
          <CardBody pt={0}>
            <Text fontSize="sm" fontWeight="bold">CPTs: <Badge colorScheme="purple">{results.context?.cpt_codes?.join(', ') || 'None'}</Badge></Text>
            <Text fontSize="sm" mt={1}>Payer: {results.context?.payer}</Text>
            <Text fontSize="sm">Carrier: {results.context?.carrier}</Text>
            <Text fontSize="sm">State: {results.context?.state}</Text>
          </CardBody>
        </Card>
      </HStack>

      {/* Ordered Diagnoses Table */}
      <Card>
        <CardHeader>
          <Heading size="md">Ordered Diagnoses (CMS-1500 Box 21)</Heading>
        </CardHeader>
        <CardBody>
          {results.ordered_diagnoses && results.ordered_diagnoses.length > 0 ? (
            <Box overflowX="auto">
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th>Pointer</Th>
                    <Th>Code</Th>
                    <Th>Description</Th>
                    <Th>Classification</Th>
                    <Th>Policy Match</Th>
                    <Th isNumeric>Score</Th>
                    <Th>Rationale</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {results.ordered_diagnoses.map((dx, idx) => (
                    <Tr key={idx} bg={highlightCodes.includes(dx.code) ? "green.50" : dx.needs_review ? "yellow.50" : "transparent"}>
                      <Td fontWeight="bold">{dx.pointer}</Td>
                      <Td fontWeight="bold">
                        {dx.code}
                        {dx.tech_report_only && (
                          <Tooltip label="This code was only found in the IONM Tech Report, not in the clinical documentation." placement="top">
                            <WarningIcon color="orange.400" ml={2} boxSize={3} />
                          </Tooltip>
                        )}
                        {dx.needs_review && (
                          <Tooltip label="This code requires review. It may not have been found in valid hospital documentation." placement="top">
                            <WarningIcon color="yellow.500" ml={2} boxSize={3} />
                          </Tooltip>
                        )}
                      </Td>
                      <Td>{dx.description}</Td>
                      <Td>
                        <Badge colorScheme={
                          dx.classification === 'Primary' ? 'green' :
                          dx.classification === 'Secondary' ? 'blue' :
                          dx.classification === 'Non-Covered' ? 'red' : 'gray'
                        }>
                          {dx.classification}
                        </Badge>
                      </Td>
                      <Td>
                        {dx.policy_match ? (
                          <Badge colorScheme="green"><CheckCircleIcon mr={1}/> Yes</Badge>
                        ) : (
                          <Badge colorScheme="red">No</Badge>
                        )}
                      </Td>
                      <Td isNumeric fontWeight="bold" color={dx.confidence_score >= 80 ? "green.600" : dx.confidence_score >= 60 ? "yellow.600" : "red.600"}>
                        {dx.confidence_score}
                      </Td>
                      <Td fontSize="xs" maxW="300px">
                      <Tooltip label={dx.rationale} placement="top" hasArrow bg="gray.700" color="white" p={2} borderRadius="md">
                        <Text noOfLines={2} cursor="help">
                          {dx.rationale}
                        </Text>
                      </Tooltip>
                    </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          ) : (
            <Text color="gray.500">No ordered diagnoses found.</Text>
          )}
        </CardBody>
      </Card>

      {/* Codes Not Selected (Dropped Codes) */}
      {results.codes_not_selected && results.codes_not_selected.length > 0 && (
        <Card borderLeft="4px solid" borderColor="gray.400">
          <CardHeader pb={2}>
            <Heading size="sm" color="gray.700">Codes Considered but Not Selected</Heading>
            <Text fontSize="xs" color="gray.500">These codes were evaluated during the pipeline but were dropped before final sequencing.</Text>
          </CardHeader>
          <CardBody pt={0}>
            <Box overflowX="auto">
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th>Code</Th>
                    <Th>Description</Th>
                    <Th>Reason</Th>
                    <Th isNumeric>Score</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {results.codes_not_selected.map((dx, idx) => {
                    let displayReason = dx.reason;
                    if (dx.drop_stage === "USN Excluded Codes") {
                      displayReason = "Excluded by USN policy guidelines.";
                    } else if (dx.drop_stage === "Evidence Extraction") {
                      displayReason = "Did not meet evidence bar.";
                    } else if (dx.drop_stage === "Scoring Task") {
                      displayReason = "Did not meet clinical confidence threshold.";
                    } else if (dx.drop_stage === "Clinical Redundancy Filter") {
                      displayReason = "Clinically redundant or overlapping with a more specific code.";
                    } else if (dx.drop_stage === "CDC Excludes Logic") {
                      displayReason = "Removed due to CDC Excludes conflict.";
                    } else if (dx.drop_stage === "Sequencing Task") {
                      displayReason = "Omitted to keep claim concise (limited to 5 diagnoses).";
                    }

                    return (
                      <Tr key={idx}>
                        <Td fontWeight="bold" color="gray.600">{dx.code}</Td>
                        <Td color="gray.600">{dx.description}</Td>
                        <Td fontSize="xs" whiteSpace="normal" color="gray.600">{displayReason}</Td>
                        <Td isNumeric color="gray.600">{dx.score !== null ? dx.score : '-'}</Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </Box>
          </CardBody>
        </Card>
      )}

      {/* Invalid / Dropped Diagnoses Table */}
      {results.invalid_diagnoses && results.invalid_diagnoses.length > 0 && (
        <Card borderLeft="4px solid" borderColor="red.400">
          <CardHeader>
            <Heading size="md" color="red.600" display="flex" alignItems="center">
              <WarningIcon mr={2} />
              Invalid / Dropped Diagnoses
            </Heading>
          </CardHeader>
          <CardBody pt={0}>
            <Box overflowX="auto">
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th>Code</Th>
                    <Th>Description</Th>
                    <Th>Classification</Th>
                    <Th isNumeric>Score</Th>
                    <Th>Rationale</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {results.invalid_diagnoses.map((dx, idx) => (
                    <Tr key={idx}>
                      <Td fontWeight="bold" color="red.600">{dx.code}</Td>
                      <Td>{dx.description}</Td>
                      <Td>
                        <Badge colorScheme="red">
                          {dx.classification || 'Invalid'}
                        </Badge>
                      </Td>
                      <Td isNumeric>{dx.confidence_score}</Td>
                      <Td fontSize="xs" maxW="300px">
                      <Tooltip label={dx.rationale} placement="top" hasArrow bg="gray.700" color="white" p={2} borderRadius="md">
                        <Text noOfLines={2} cursor="help">
                          {dx.rationale}
                        </Text>
                      </Tooltip>
                    </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          </CardBody>
        </Card>
      )}

      {/* LCD/NCD Rule Evaluations */}
      {results.lcd_ncd_evaluations && results.lcd_ncd_evaluations.length > 0 && (
        <Card>
          <CardHeader>
            <Heading size="md">LCD/NCD Rule Evaluations</Heading>
          </CardHeader>
          <CardBody>
            <Accordion allowMultiple>
              {results.lcd_ncd_evaluations.map((rule, idx) => (
                <AccordionItem key={idx}>
                  <h2>
                    <AccordionButton>
                      <Box flex="1" textAlign="left" fontWeight="bold">
                        {rule.rule_id}: {rule.rule_name}
                        <Badge ml={2} colorScheme={rule.met ? 'green' : 'red'}>
                          {rule.met ? 'Met' : 'Not Met'}
                        </Badge>
                      </Box>
                      <AccordionIcon />
                    </AccordionButton>
                  </h2>
                  <AccordionPanel pb={4}>
                    <Text fontSize="sm" mb={2}><strong>Description:</strong> {rule.description}</Text>
                    <Text fontSize="sm" mb={2}><strong>Evaluation:</strong> {rule.evaluation}</Text>
                    {rule.missing_requirements && rule.missing_requirements.length > 0 && (
                      <Box mt={2}>
                        <Text fontSize="sm" fontWeight="bold" color="red.600">Missing Requirements:</Text>
                        <List spacing={1} mt={1}>
                          {rule.missing_requirements.map((req, i) => (
                            <ListItem key={i} fontSize="sm">
                              <ListIcon as={WarningIcon} color="red.500" />
                              {req}
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    )}
                  </AccordionPanel>
                </AccordionItem>
              ))}
            </Accordion>
          </CardBody>
        </Card>
      )}

      {/* Policy Discovery Tracking */}
      {results.policy_tracking && Object.keys(results.policy_tracking).length > 0 && (
        <Card>
          <CardHeader>
            <Heading size="md">Policy Discovery by CPT</Heading>
          </CardHeader>
          <CardBody>
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th>CPT Code</Th>
                  <Th>NCDs Found</Th>
                  <Th>LCDs Found</Th>
                  <Th>Articles Found</Th>
                </Tr>
              </Thead>
              <Tbody>
                {Object.entries(results.policy_tracking).map(([cpt, tracking]) => (
                  <Tr key={cpt}>
                    <Td fontWeight="bold">{cpt}</Td>
                    <Td>
                      {tracking.NCD.length > 0 ? (
                        tracking.NCD.map(id => <Badge key={id} colorScheme="green" mr={1}>{id}</Badge>)
                      ) : (
                        <Text color="gray.400" fontSize="xs">None</Text>
                      )}
                    </Td>
                    <Td>
                      {tracking.LCD.length > 0 ? (
                        tracking.LCD.map(id => <Badge key={id} colorScheme="blue" mr={1}>{id}</Badge>)
                      ) : (
                        <Text color="gray.400" fontSize="xs">None</Text>
                      )}
                    </Td>
                    <Td>
                      {tracking.Article.length > 0 ? (
                        tracking.Article.map(id => <Badge key={id} colorScheme="purple" mr={1}>{id}</Badge>)
                      ) : (
                        <Text color="gray.400" fontSize="xs">None</Text>
                      )}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </CardBody>
        </Card>
      )}

      {/* LCD Relevancy Scoring (Evaluation Section) */}
      {results.lcd_relevancy_analysis && (
        <Accordion allowToggle>
          <AccordionItem border="none">
            <Card borderLeft="4px solid" borderColor="purple.400">
              <CardHeader p={0}>
                <AccordionButton _hover={{ bg: 'purple.50' }} p={4}>
                  <Box flex="1" textAlign="left">
                    <Heading size="md" color="purple.700">LCD Relevancy Evaluation (Beta)</Heading>
                    <Text fontSize="sm" color="gray.600" mt={1}>
                      AI-powered assessment of which candidate LCDs are most relevant to the clinical documentation.
                    </Text>
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </CardHeader>
              <AccordionPanel pb={4}>
                <CardBody pt={0}>
                  <VStack align="stretch" spacing={4}>
                    {results.lcd_relevancy_analysis.discussion && (
                      <Box p={3} bg="gray.50" borderRadius="md" borderWidth="1px" borderColor="gray.200">
                        <Text fontSize="sm" fontWeight="semibold" mb={1}>Clinical Context Analysis:</Text>
                        <Text fontSize="sm">{results.lcd_relevancy_analysis.discussion}</Text>
                      </Box>
                    )}
                    
                    {results.lcd_relevancy_analysis.relevancy_scores && results.lcd_relevancy_analysis.relevancy_scores.length > 0 && (
                      <Box>
                        <Text fontSize="sm" fontWeight="semibold" mb={2}>Candidate Policy Scores:</Text>
                        <Table variant="simple" size="sm">
                          <Thead>
                            <Tr>
                              <Th>Policy ID</Th>
                              <Th>Relevancy Score</Th>
                              <Th>Reason</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {results.lcd_relevancy_analysis.relevancy_scores
                              .sort((a, b) => b.score - a.score)
                              .map((scoreItem, idx) => (
                              <Tr key={idx}>
                                <Td fontWeight="bold" color="blue.600">{scoreItem.policy_id || scoreItem.lcd_id}</Td>
                                <Td>
                                  <Badge colorScheme={scoreItem.score >= 80 ? 'green' : scoreItem.score >= 40 ? 'yellow' : 'red'}>
                                    {scoreItem.score}/100
                                  </Badge>
                                </Td>
                                <Td fontSize="sm" whiteSpace="normal">{scoreItem.reason}</Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </Box>
                    )}
                  </VStack>
                </CardBody>
              </AccordionPanel>
            </Card>
          </AccordionItem>
        </Accordion>
      )}

      {/* Excludes Analysis */}
      {results.excludes_analysis && (
        <Accordion allowToggle>
          <AccordionItem border="none">
            <Card borderLeft="4px solid" borderColor={results.excludes_analysis.conflicts_found?.length > 0 ? "orange.400" : "green.400"}>
              <CardHeader p={0}>
                <AccordionButton _hover={{ bg: results.excludes_analysis.conflicts_found?.length > 0 ? 'orange.50' : 'green.50' }} p={4}>
                  <Box flex="1" textAlign="left">
                    <Heading size="md" display="flex" alignItems="center">
                      {results.excludes_analysis.conflicts_found?.length > 0 ? (
                        <WarningIcon color="orange.400" mr={2} />
                      ) : (
                        <CheckCircleIcon color="green.400" mr={2} />
                      )}
                      CDC Excludes Conflict Resolution
                    </Heading>
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </CardHeader>
              <AccordionPanel pb={4}>
                <CardBody pt={0}>
                  {results.excludes_analysis.conflicts_found?.length > 0 ? (
                    <>
                      <Text fontSize="sm" mb={4}>
                        The pipeline detected CDC coding conflicts among the extracted diagnoses. An AI review was performed to resolve them based on the clinical documentation.
                      </Text>
                      
                      <HStack align="stretch" spacing={6}>
                        <Box flex={1}>
                          <Text fontWeight="bold" fontSize="sm" mb={2}>Conflicts Detected:</Text>
                          <List spacing={2}>
                            {results.excludes_analysis.conflicts_found.map((conflict, idx) => (
                              <ListItem key={idx} fontSize="sm" bg="gray.50" p={2} borderRadius="md">
                                <Badge colorScheme={conflict.type === 'Excludes1' ? 'red' : 'orange'} mr={2}>
                                  {conflict.type}
                                </Badge>
                                <Text as="span" fontWeight="bold">{conflict.code1}</Text> and <Text as="span" fontWeight="bold">{conflict.code2}</Text>
                                <Text fontSize="xs" color="gray.600" mt={1}>{conflict.description}</Text>
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                        
                        <Box flex={1}>
                          <Text fontWeight="bold" fontSize="sm" mb={2}>AI Resolution:</Text>
                          <Box bg="blue.50" p={3} borderRadius="md" height="100%">
                            <HStack mb={2}>
                              <Badge colorScheme="green">Kept: {results.excludes_analysis.ai_resolution?.keep?.join(', ') || 'None'}</Badge>
                              <Badge colorScheme="red">Dropped: {results.excludes_analysis.ai_resolution?.drop?.join(', ') || 'None'}</Badge>
                            </HStack>
                            <Text fontSize="sm" fontStyle="italic">
                              "{results.excludes_analysis.ai_resolution?.rationale}"
                            </Text>
                          </Box>
                        </Box>
                      </HStack>
                    </>
                  ) : (
                    <Text fontSize="sm" color="gray.600">
                      No CDC Excludes1 or Excludes2 conflicts were detected among the extracted diagnoses.
                    </Text>
                  )}
                </CardBody>
              </AccordionPanel>
            </Card>
          </AccordionItem>
        </Accordion>
      )}

      {/* Warnings & Rationale */}
      <HStack spacing={6} align="stretch">
        <Accordion allowToggle flex={1}>
          <AccordionItem border="none">
            <Card h="100%">
              <CardHeader p={0}>
                <AccordionButton _hover={{ bg: 'orange.50' }} p={4}>
                  <Box flex="1" textAlign="left">
                    <Heading size="md" display="flex" alignItems="center">
                      <WarningIcon color="orange.400" mr={2} /> Assessment Score
                    </Heading>
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </CardHeader>
              <AccordionPanel pb={4}>
                <CardBody pt={0}>
                  {results.approval_assessment && (
                    <Box mb={4} fontSize="sm" color="gray.500" sx={{ '& p': { marginBottom: '1em' } }}>
                      <Text fontWeight="bold" mb={1}>Legacy Assessment:</Text>
                      <ReactMarkdown>{results.approval_assessment}</ReactMarkdown>
                    </Box>
                  )}
                  {results.approval_assessment_v2 && (
                    <Box mb={4} fontSize="sm" color="gray.700" sx={{ '& p': { marginBottom: '1em' } }}>
                      <Text fontWeight="bold" mb={1}>V2 Assessment:</Text>
                      <ReactMarkdown>{results.approval_assessment_v2}</ReactMarkdown>
                    </Box>
                  )}
                  {results.warnings && results.warnings.length > 0 ? (
                    <List spacing={2}>
                      {results.warnings.map((warning, idx) => (
                        <ListItem key={idx} fontSize="sm">
                          <ListIcon as={WarningIcon} color="orange.400" />
                          {warning}
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    !results.approval_assessment && !results.approval_assessment_v2 && <Text color="gray.500" fontSize="sm">No warnings generated.</Text>
                  )}
                </CardBody>
              </AccordionPanel>
            </Card>
          </AccordionItem>
        </Accordion>

        <Accordion allowToggle flex={1}>
          <AccordionItem border="none">
            <Card h="100%">
              <CardHeader p={0}>
                <AccordionButton _hover={{ bg: 'blue.50' }} p={4}>
                  <Box flex="1" textAlign="left">
                    <Heading size="md" display="flex" alignItems="center">
                      <InfoIcon color="blue.400" mr={2} /> Pipeline Rationale
                    </Heading>
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </CardHeader>
              <AccordionPanel pb={4}>
                <CardBody pt={0}>
                  {results.rationale && results.rationale.length > 0 ? (
                    <List spacing={2}>
                      {results.rationale.map((rat, idx) => (
                        <ListItem key={idx} fontSize="sm">
                          <ListIcon as={CheckCircleIcon} color="green.400" />
                          {rat}
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Text color="gray.500" fontSize="sm">No rationale provided.</Text>
                  )}
                </CardBody>
              </AccordionPanel>
            </Card>
          </AccordionItem>
        </Accordion>
      </HStack>

    </VStack>
  );
};

export default MedicalNecessityPanel;
